import React, { useState, useMemo, useCallback } from "react";
import { Formik, FormikProps } from "formik";
import classNames from "classnames";
import nextId from "react-id-generator";

import { ProcedureImportStatusType } from "../../../../../application/domain/procedures/procedure-import-status";
import { BaseModal } from "../../../../atoms/base-modal";
import { GroupShareSelectField } from "../../../../molecules/group-share-select-field";
import { matchType } from "../../../../../application/domain/base/base-type";
import { emptyFunction } from "../../../../../utils/function-utils";

type DomKeyFileNamePair = {
  reactDomKey: string;
  fileName: string;
};

const MAX_FILE_COUNT = 30;

export const ProcedureImportFromFileModal: React.FC<{
  orgId: string;
  isActive: boolean;
  isLoading: boolean;
  close: () => void;
  onSave: (
    jsonFiles: File[],
    groupId: string,
    customPathClearTargets: string[],
  ) => Promise<{
    importedProcIds: string[];
    errorMessages: string[];
  }>;
  checkImportFile: (
    jsonData: File,
    orgId: string,
  ) => Promise<ProcedureImportStatusType | void>;
}> = ({ orgId, isActive, isLoading, close, onSave, checkImportFile }) => {
  const [isCheckingImportFile, setIsCheckingImportFile] =
    useState<boolean>(false);
  const [jsonFiles, setJsonFiles] = useState<File[]>([]);
  const [groupId, setGroupId] = useState<string>();
  const [invalidJsons, setInvalidJsons] = useState<DomKeyFileNamePair[]>([]);
  const [invalidFormSchemas, setInvalidFormSchemas] = useState<
    DomKeyFileNamePair[]
  >([]);
  const [duplicatedCustomPathFileNames, setDuplicatedCustomPathFileNames] =
    useState<string[]>([]);
  const [customPathClearTargets, setCustomPathClearTargets] = useState<
    string[]
  >([]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [importedProcIds, setImportedProcIds] = useState<string[]>([]);

  const [pageStatus, setPageStatus] = useState<"UPLOAD_FILE" | "RESULT">(
    "UPLOAD_FILE",
  );

  const canImportWithCustomPathSetting = useMemo((): boolean => {
    return (
      jsonFiles.length <= MAX_FILE_COUNT &&
      invalidJsons.length === 0 &&
      invalidFormSchemas.length === 0 &&
      duplicatedCustomPathFileNames.filter(
        (v) => !customPathClearTargets.includes(v),
      ).length === 0
    );
  }, [
    jsonFiles,
    invalidJsons,
    invalidFormSchemas,
    duplicatedCustomPathFileNames,
    customPathClearTargets,
  ]);

  const checkFile = async (data: File, reactDomKey: string) => {
    setIsCheckingImportFile(true);
    const result = await checkImportFile(data, orgId);
    if (result !== undefined) {
      if (result.hasDuplicateCustomPath)
        setDuplicatedCustomPathFileNames((f) => [...f, data.name]);
      if (result.hasInvalidJson)
        setInvalidJsons((f) => [...f, { reactDomKey, fileName: data.name }]);

      if (result.hasInvalidFormSchema) {
        setInvalidFormSchemas((curr) => {
          return [...curr, { reactDomKey, fileName: data.name }];
        });
      }
    }
    setIsCheckingImportFile(false);
  };

  const initForm = () => {
    setJsonFiles([]);
    setCustomPathClearTargets([]);
    setDuplicatedCustomPathFileNames([]);
    setInvalidJsons([]);
    setInvalidFormSchemas([]);
  };

  const initResults = () => {
    setErrorMessages([]);
    setImportedProcIds([]);
  };

  const handleClose = useCallback(() => {
    setPageStatus("UPLOAD_FILE");
    initResults();
    close();
  }, [close, setPageStatus, initResults]);

  return (
    <BaseModal
      isActive={isActive}
      title="手続きインポート"
      onClose={handleClose}
      hasOverflow={true}
    >
      {matchType(pageStatus, {
        UPLOAD_FILE: (
          <Formik
            validateOnChange={true}
            initialValues={{}}
            onSubmit={emptyFunction}
          >
            {({ handleSubmit }: FormikProps<object>) => (
              <form onSubmit={handleSubmit}>
                <section className="modal-card-body">
                  <div className="field">
                    <label className="label">手続き情報ファイル</label>
                    <div
                      className={classNames("file has-name", {
                        "is-danger": !canImportWithCustomPathSetting,
                      })}
                    >
                      <label className="file-label">
                        <input
                          className="file-input"
                          type="file"
                          multiple
                          name="resume"
                          accept={"application/json"}
                          onChange={({ target: { files } }) => {
                            if (!files) {
                              return;
                            }
                            initForm();
                            for (let i = 0; i < files.length; i++) {
                              const file = files[i];
                              if (file instanceof Blob) {
                                const fileReader = new FileReader();
                                fileReader.readAsText(file);
                                fileReader.onload = function () {
                                  checkFile(file, nextId());
                                  setJsonFiles((j) => [...j, file]);
                                };
                              }
                            }
                          }}
                        />
                        <span className="file-cta">
                          <span className="file-icon">
                            <i className="fas fa-upload" />
                          </span>
                          <span className="file-label">
                            {jsonFiles.length === 0
                              ? "ファイルを選択…"
                              : jsonFiles.length === 1
                                ? jsonFiles[0]?.name
                                : `計${jsonFiles.length}件`}
                          </span>
                        </span>
                        <span className="file-name">
                          {jsonFiles.length > 0 ? "選択済み" : "未選択"}
                        </span>
                      </label>
                    </div>
                    {!canImportWithCustomPathSetting && (
                      <CheckImportErrorMessages
                        jsonFiles={jsonFiles}
                        invalidJsons={invalidJsons}
                        duplicatedCustomPathFileNames={
                          duplicatedCustomPathFileNames
                        }
                        invalidFormSchemas={invalidFormSchemas}
                      />
                    )}
                  </div>
                  {jsonFiles.length <= MAX_FILE_COUNT &&
                    invalidJsons.length === 0 &&
                    invalidFormSchemas.length === 0 &&
                    duplicatedCustomPathFileNames.length > 0 && (
                      <div className="field">
                        <p className="help is-info">
                          対象ファイルをチェックしてインポートすると、このファイルに含まれる申請開始ページURLの設定は削除され、
                          申請開始ページURLが設定されていない手続きとしてインポートされます。
                        </p>
                        <ul>
                          {duplicatedCustomPathFileNames.map((filename) => {
                            return (
                              <li>
                                <label className="checkbox">
                                  <input
                                    type="checkbox"
                                    checked={customPathClearTargets.includes(
                                      filename,
                                    )}
                                    onChange={() => {
                                      setCustomPathClearTargets((v) =>
                                        customPathClearTargets.includes(
                                          filename,
                                        )
                                          ? v.filter((n) => n !== filename)
                                          : [...v, filename],
                                      );
                                    }}
                                  />
                                  &nbsp; {filename}
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  <div className="field">
                    <GroupShareSelectField
                      tenantId={orgId}
                      selectedGroupId={groupId || ""}
                      onChange={setGroupId}
                    />
                  </div>
                  {errorMessages.length > 0 && (
                    <ul
                      className="help is-danger pl-4"
                      style={{ listStyleType: "disc" }}
                    >
                      {errorMessages.map((message) => (
                        <li>{message}</li>
                      ))}
                    </ul>
                  )}
                </section>
                <section className="modal-card-foot">
                  <button
                    type="button"
                    className="button is-failure is-fullwidth"
                    onClick={handleClose}
                  >
                    キャンセルする
                  </button>
                  <button
                    type="submit"
                    className={classNames("button is-info is-fullwidth", {
                      "is-loading": isLoading || isCheckingImportFile,
                    })}
                    onClick={async () => {
                      if (
                        jsonFiles.length === 0 ||
                        !groupId ||
                        !canImportWithCustomPathSetting
                      ) {
                        return;
                      }
                      const { importedProcIds, errorMessages } = await onSave(
                        jsonFiles,
                        groupId,
                        customPathClearTargets,
                      );
                      setErrorMessages(errorMessages);
                      setImportedProcIds(importedProcIds);
                      setPageStatus("RESULT");
                    }}
                    disabled={
                      jsonFiles.length === 0 ||
                      !groupId ||
                      !canImportWithCustomPathSetting ||
                      isLoading ||
                      isCheckingImportFile
                    }
                  >
                    インポートする
                  </button>
                </section>
              </form>
            )}
          </Formik>
        ),
        RESULT: (
          <>
            <section className="modal-card-body">
              <div className="field">
                <label className="label">インポート終了</label>
              </div>
              {importedProcIds.length}件の手続きをインポートしました。
              {errorMessages.length > 0 && (
                <div className="mt-3">
                  以下の手続きのインポートに失敗しました。
                  <ul
                    className="help is-danger pl-4"
                    style={{ listStyleType: "disc" }}
                  >
                    {errorMessages.map((message) => (
                      <li>{message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
            <section className="modal-card-foot">
              <button
                type="button"
                className="button is-failure is-fullwidth"
                onClick={handleClose}
              >
                閉じる
              </button>
            </section>
          </>
        ),
      })}
    </BaseModal>
  );
};

const CheckImportErrorMessages: React.FC<{
  jsonFiles: File[];
  invalidJsons: DomKeyFileNamePair[];
  duplicatedCustomPathFileNames: string[];
  invalidFormSchemas: DomKeyFileNamePair[];
}> = ({
  jsonFiles,
  invalidJsons,
  duplicatedCustomPathFileNames,
  invalidFormSchemas,
}) => {
  return (
    <>
      {jsonFiles.length > MAX_FILE_COUNT && (
        <p className="help is-danger">
          ファイルは{MAX_FILE_COUNT}まで選択できます。
        </p>
      )}
      {invalidJsons.length > 0 && (
        <>
          <p className="help is-danger">
            JSONファイルの形式が正しくないファイルがあります。
          </p>
          <ul>
            {invalidJsons.map(({ fileName, reactDomKey }) => (
              <li key={reactDomKey}>{fileName}</li>
            ))}
          </ul>
        </>
      )}
      {duplicatedCustomPathFileNames.length > 0 && (
        <p className="help is-danger">
          申請開始ページURLがすでに登録されているファイルがあります。
        </p>
      )}
      {invalidFormSchemas.length > 0 && (
        <>
          <p className="help is-danger">
            JSONファイル内のFormSchemaが正しく設定されていないファイルが存在します。
          </p>
          <ul>
            {invalidFormSchemas.map(({ fileName, reactDomKey }) => (
              <li key={reactDomKey}>{fileName}</li>
            ))}
          </ul>
        </>
      )}
    </>
  );
};
